// P17b — Page /sites : liste CRUD complète
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Trash2, Plus } from "lucide-react";
import { useCan } from "@/hooks/useCan";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";
import { useT } from "@/lib/i18n";
interface Site {
  id: number;
  name: string;
  client: string | null;
  adresse: string | null;
  created_at?: string;
  updated_at?: string;
}

async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const sitesApi = {
  list: () => fetchJson<Site[]>(`/v1/sites`),
  create: (data: { name: string; client?: string; adresse?: string }) =>
    fetchJson<Site>(`/v1/sites`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: number, data: Partial<Site>) =>
    fetchJson<Site>(`/v1/sites/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  remove: (id: number) => fetchJson<void>(`/v1/sites/${id}`, { method: "DELETE" }),
};

function SiteModal({
  onClose,
  initial,
}: {
  onClose: () => void;
  initial?: Site;
}) {
  const t = useT();
  const { toast } = useToast();
  const [name, setName] = useState(initial?.name ?? "");
  const [client, setClient] = useState(initial?.client ?? "");
  const [adresse, setAdresse] = useState(initial?.adresse ?? "");
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      const data = {
        name: name.trim(),
        client: client.trim() || undefined,
        adresse: adresse.trim() || undefined,
      };
      return initial ? sitesApi.update(initial.id, data) : sitesApi.create(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sites"] });
      onClose();
      toast({
        title: initial ? "Site modifié" : "Site créé",
        variant: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Erreur",
        description: (err as Error).message,
        variant: "error",
      });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(32rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>{initial ? "Modifier" : t("common.new")} site</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <label className="text-xs">{ht("Nom *")}</label>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={ht("Ex: LOG-GABON OIL COMPANY")}
              />
            </div>
            <div>
              <label className="text-xs">{ht("Client")}</label>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={client}
                onChange={(e) => setClient(e.target.value)}
                placeholder={ht("Ex: POG SA")}
              />
            </div>
            <div>
              <label className="text-xs">{ht("Adresse")}</label>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={adresse}
                onChange={(e) => setAdresse(e.target.value)}
                placeholder={ht("Ex: Tsiengeui, Gabon")}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>{ht("Annuler")}
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={!name.trim() || mutation.isPending}
            >
              {mutation.isPending ? "En cours..." : initial ? "Modifier" : "Créer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ListeSitesPage() {
  const t = useT();
  const [showCreate, setShowCreate] = useState(false);
  const [edit, setEdit] = useState<Site | null>(null);
  const [del, setDel] = useState<Site | null>(null);
    const { toast } = useToast();
  const qc = useQueryClient();
  const canEdit = useCan("write");

  const { data: sites, isLoading } = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => sitesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sites"] });
      toast({ title: ht("Site supprimé"), variant: "success" });
      setDel(null);
    },
    onError: (err) => {
      const msg = (err as Error).message;
      const title = msg.startsWith(t("articles.error.delete_impossible"))
        ? t("articles.toast.delete_impossible")
        : t("articles.toast.delete_error");
      toast({
        title,
        description: msg,
        variant: "error",
        duration: 8000,
      });
      setDel(null);
    },
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{ht("Sites")}</h1>
          <p className="text-muted-foreground">
            {t("sites.subtitle")} ({sites?.length ?? 0} {t("common.sites")})
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t("common.new")} {t("common.site").toLowerCase()}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{ht("Liste des sites")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">{ht("Chargement...")}</div>
          ) : !sites || sites.length === 0 ? (
            <div className="text-sm text-muted-foreground">{ht("Aucun site enregistré.")}</div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2">{ht("ID")}</th>
                  <th className="px-3 py-2">{ht("Nom")}</th>
                  <th className="px-3 py-2">{ht("Client")}</th>
                  <th className="px-3 py-2">{ht("Adresse")}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sites.map((s) => (
                  <tr key={s.id} className="group border-b hover:bg-muted/30">
                    <td className="px-3 py-2 font-mono">#{s.id}</td>
                    <td className="px-3 py-2 font-semibold">{s.name}</td>
                    <td className="px-3 py-2">{s.client ?? "-"}</td>
                    <td className="px-3 py-2 text-xs">{s.adresse ?? "-"}</td>
                    <td className="px-3 py-2">
                      {canEdit && (
                        <div className="flex gap-1 opacity-70 hover:opacity-100">
                          <button
                            onClick={() => setEdit(s)}
                            className="rounded p-1 text-xs text-muted-foreground hover:bg-muted"
                          >{ht("Éditer")}
                          </button>
                          <button
                            onClick={() => setDel(s)}
                            className="rounded p-1 text-muted-foreground hover:bg-destructive hover:text-destructive-foreground"
                            title={ht("Supprimer")}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </CardContent>
      </Card>

      {showCreate && <SiteModal onClose={() => setShowCreate(false)} />}
      {edit && <SiteModal onClose={() => setEdit(null)} initial={edit} />}

      <ConfirmDialog
        open={del !== null}
        title={ht("Supprimer ce site ?")}
        description={`Cette action supprimera définitivement le site "${del?.name ?? ""}". Irréversible.`}
        confirmLabel={ht("Supprimer")}
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => del && deleteMutation.mutate(del.id)}
        onCancel={() => setDel(null)}
      />
    </div>
  );
}
